import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import {
	Button,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, EnvironmentOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import PageTitle from "../../page-header/PageHeader";
import {
	deleteProjectSiteDetail,
	loadProjectSiteDetails,
	loadProjectTypes,
	saveProjectSiteDetail,
} from "../../../redux/rtk/features/projectManagement/project/projectSite/projectSiteDetails";

const parseProjectTypeIds = (value) => {
	if (Array.isArray(value)) {
		return value
			.map((item) => Number(item))
			.filter((num) => Number.isFinite(num))
			.filter((num, index, arr) => arr.indexOf(num) === index);
	}
	if (typeof value === "string") {
		return value
			.split(",")
			.map((item) => Number(item.trim()))
			.filter((num) => Number.isFinite(num))
			.filter((num, index, arr) => arr.indexOf(num) === index);
	}
	if (value !== undefined && value !== null) {
		const num = Number(value);
		return Number.isFinite(num) ? [num] : [];
	}
	return [];
};

const formatProjectTypeNames = (ids = [], lookup = {}) => {
	if (!Array.isArray(ids) || ids.length === 0) {
		return "-";
	}
	return ids
		.map((id) => lookup[id] || `Type ${id}`)
		.join(", ");
};

const ProjectSiteDetails = () => {
	const dispatch = useDispatch();
	const {
		list = [],
		projectTypes = [],
		loading,
		typesLoading = false,
	} = useSelector((state) => state.projectSiteDetails || {});
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [modalVisible, setModalVisible] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [editingRecord, setEditingRecord] = useState(null);
	const [isBulkMode, setIsBulkMode] = useState(false);

	useEffect(() => {
		dispatch(loadProjectSiteDetails());
		dispatch(loadProjectTypes());
	}, [dispatch]);

	const projectTypeMap = useMemo(() => {
		if (!Array.isArray(projectTypes)) {
			return {};
		}
		return projectTypes.reduce((acc, type) => {
			const rawId = type?.ProjectTypeId ?? type?.id;
			const id = Number(rawId);
			const name = type?.ProjectTypeName ?? type?.name;
			if (Number.isFinite(id) && name) {
				acc[id] = name;
			}
			return acc;
		}, {});
	}, [projectTypes]);

	const projectTypeOptions = useMemo(() => {
		if (!Array.isArray(projectTypes)) {
			return [];
		}
		return projectTypes
			.map((type) => {
				const rawId = type?.ProjectTypeId ?? type?.id;
				const id = Number(rawId);
				const name = type?.ProjectTypeName ?? type?.name;
				if (!Number.isFinite(id) || !name) {
					return null;
				}
				return {
					value: id,
					label: name,
				};
			})
			.filter(Boolean);
	}, [projectTypes]);

	const dataSource = useMemo(() => {
		if (!Array.isArray(list)) {
			return [];
		}
		return list.map((item, index) => {
			const parsedTypeIds = parseProjectTypeIds(
				item?.ProjectTypeIds ?? item?.ProjectTypeId
			);
			return {
				...item,
				ProjectTypeIdsParsed: parsedTypeIds,
				ProjectTypeDisplay: formatProjectTypeNames(parsedTypeIds, projectTypeMap),
				key: item.ProjectSiteId ?? index,
			};
		});
	}, [list, projectTypeMap]);

	const getEmptyRow = useCallback(
		() => ({
			ProjectSiteName: "",
			ProjectTypeIds: [],
			OnboardDate: null,
			ProjectTimeLine: null,
		}),
		[]
	);

	const openAddModal = useCallback(() => {
		setEditingRecord(null);
		setIsBulkMode(true);
		form.resetFields();
		form.setFieldsValue({
			sites: [getEmptyRow()],
		});
		setModalVisible(true);
	}, [form, getEmptyRow]);

	const openEditModal = useCallback(
		(record) => {
			setEditingRecord(record);
			setIsBulkMode(false);
			form.resetFields();
			form.setFieldsValue({
				ProjectSiteName: record?.ProjectSiteName ?? "",
				ProjectTypeIds: parseProjectTypeIds(
					record?.ProjectTypeIds ?? record?.ProjectTypeId
				),
				OnboardDate: record?.OnboardDate ? dayjs(record.OnboardDate) : null,
				ProjectTimeLine:
					record?.ProjectTimeLine !== undefined && record?.ProjectTimeLine !== null
						? Number(record.ProjectTimeLine)
						: null,
			});
			setModalVisible(true);
		},
		[form]
	);

	const closeModal = useCallback(() => {
		setModalVisible(false);
		setEditingRecord(null);
		setIsBulkMode(false);
		form.resetFields();
	}, [form]);

	const handleManageSiteArea = useCallback(
		(record) => {
			const siteId = record?.ProjectSiteId ?? record?.id;
			if (!siteId) {
				return;
			}
			navigate("/admin/project/site-area", {
				state: {
					projectSiteId: Number(siteId),
				},
			});
		},
		[navigate]
	);

	const buildPayload = useCallback((site, overrideId = 0) => {
		const resolvedId =
			overrideId !== undefined && overrideId !== null ? Number(overrideId) : 0;
		const projectTypeIds = parseProjectTypeIds(site?.ProjectTypeIds);
		return {
			ProjectSiteId: Number.isFinite(resolvedId) ? resolvedId : 0,
			ProjectSiteName: site?.ProjectSiteName?.trim(),
			ProjectTypeIds: projectTypeIds,
			OnboardDate: site?.OnboardDate
				? dayjs(site.OnboardDate).format("YYYY-MM-DD")
				: null,
			ProjectTimeLine:
				site?.ProjectTimeLine !== undefined && site?.ProjectTimeLine !== null
					? Number(site.ProjectTimeLine)
					: null,
			DataStatus: 1,
		};
	}, []);

	const handleSubmit = useCallback(async () => {
		try {
			setSaving(true);
			if (isBulkMode) {
				const values = await form.validateFields();
				const sites = values?.sites || [];
				const validSites = sites.filter((site) => site?.ProjectSiteName);
				if (!validSites.length) {
					setSaving(false);
					return;
				}
				let hasError = false;
				for (const site of validSites) {
					const payload = buildPayload(site, 0);
					const response = await dispatch(saveProjectSiteDetail(payload));
					if (response?.meta?.requestStatus === "rejected") {
						hasError = true;
					}
				}
				if (!hasError) {
					closeModal();
					dispatch(loadProjectSiteDetails());
				}
			} else {
				const values = await form.validateFields();
				const parsedProjectSiteId =
					editingRecord?.ProjectSiteId !== undefined &&
					editingRecord?.ProjectSiteId !== null
						? Number(editingRecord.ProjectSiteId)
						: 0;
				const payload = buildPayload(
					values,
					Number.isFinite(parsedProjectSiteId)
						? parsedProjectSiteId
						: editingRecord?.ProjectSiteId ?? 0
				);
				const response = await dispatch(saveProjectSiteDetail(payload));
				if (response?.meta?.requestStatus !== "rejected") {
					closeModal();
					dispatch(loadProjectSiteDetails());
				}
			}
		} finally {
			setSaving(false);
		}
	}, [buildPayload, closeModal, dispatch, editingRecord, form, isBulkMode]);

	const handleDelete = useCallback(
		async (record) => {
			setDeletingId(record.ProjectSiteId);
			try {
				const response = await dispatch(
					deleteProjectSiteDetail({ ProjectSiteId: record.ProjectSiteId })
				);
				if (response?.meta?.requestStatus !== "rejected") {
					dispatch(loadProjectSiteDetails());
				}
			} finally {
				setDeletingId(null);
			}
		},
		[dispatch]
	);

	const columns = useMemo(
		() => [
			{
				title: "Project Site Name",
				dataIndex: "ProjectSiteName",
				key: "ProjectSiteName",
			},
			{
				title: "Project Types",
				dataIndex: "ProjectTypeDisplay",
				key: "ProjectTypeDisplay",
				render: (_, record) => record?.ProjectTypeDisplay || "-",
			},
			{
				title: "Onboard Date",
				dataIndex: "OnboardDate",
				key: "OnboardDate",
				render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
			},
			{
				title: "Timeline (Months)",
				dataIndex: "ProjectTimeLine",
				key: "ProjectTimeLine",
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Button
							type="link"
							icon={<EnvironmentOutlined />}
							title="Manage Site Area"
							onClick={() => handleManageSiteArea(record)}
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => openEditModal(record)}
						/>
						<Popconfirm
							title="Delete project site"
							description={`Are you sure you want to delete ${record.ProjectSiteName}?`}
							onConfirm={() => handleDelete(record)}
							okText="Yes"
							cancelText="No"
						>
							<Button
								type="link"
								danger
								icon={<DeleteOutlined />}
								loading={deletingId === record.ProjectSiteId}
							/>
						</Popconfirm>
					</Space>
				),
			},
		],
		[deletingId, handleDelete, handleManageSiteArea, openEditModal]
	);

	const isLogged = Boolean(localStorage.getItem("isLogged"));
	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<div className="project-site-details-page-wrapper">
			
			<div className="ant-card p-4 rounded mt-5 project-site-details-table-container">
				<div className="flex my-2 justify-between">
					<div className="w-50">
						<h4 className="text-xl mb-2">Project Site Details</h4>
					</div>
					<div className="flex">
					<Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
						Add Project Sites
					</Button>
					</div>
				</div>
				{loading ? (
					<div className="text-center py-8">
						<div className="ant-spin ant-spin-lg ant-spin-spinning">
							<span className="ant-spin-dot ant-spin-dot-spin">
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
							</span>
						</div>
						<p className="mt-4 text-gray-600">Loading site details...</p>
					</div>
				) : !dataSource || dataSource.length === 0 ? (
					<div className="text-center py-8">
						<p className="text-gray-600">No site details found.</p>
					</div>
				) : (
				<Table
					dataSource={dataSource}
					columns={columns}
					loading={loading}
					rowKey="key"
						scroll={{ x: true, y: "100%" }}
						pagination={{
							defaultPageSize: 20,
						}}
						className="project-site-details-table"
				/>
				)}
			</div>
			<Modal
				title={editingRecord ? "Edit Project Site" : "Add Project Sites"}
				open={modalVisible}
				onCancel={closeModal}
				onOk={handleSubmit}
				confirmLoading={saving}
				destroyOnClose
				width={900}
			>
				<Form form={form} layout="vertical">
					{isBulkMode ? (
						<Form.List name="sites">
							{(fields, { add, remove }) => (
								<>
									<Table
										dataSource={fields.map((field) => ({ ...field, key: field.key }))}
										columns={[
											{
												title: "#",
												width: 60,
												render: (_, __, index) => index + 1,
											},
											{
												title: "Project Site Name",
												dataIndex: "ProjectSiteName",
												render: (_, field) => (
													<Form.Item
														name={[field.name, "ProjectSiteName"]}
														fieldKey={[field.fieldKey, "ProjectSiteName"]}
														rules={[{ required: true, message: "Enter project site name" }]}
														style={{ marginBottom: 0 }}
													>
														<Input placeholder="Enter project site name" />
													</Form.Item>
												),
											},
											{
												title: "Project Types",
												dataIndex: "ProjectTypeIds",
												width: 240,
												render: (_, field) => (
													<Form.Item
														name={[field.name, "ProjectTypeIds"]}
														fieldKey={[field.fieldKey, "ProjectTypeIds"]}
														rules={[{ required: true, message: "Select project types" }]}
														style={{ marginBottom: 0 }}
													>
														<Select
															mode="multiple"
															placeholder="Select project types"
															options={projectTypeOptions}
															optionFilterProp="label"
															loading={typesLoading && !projectTypeOptions.length}
															allowClear
														/>
													</Form.Item>
												),
											},
											{
												title: "Onboard Date",
												dataIndex: "OnboardDate",
												render: (_, field) => (
													<Form.Item
														name={[field.name, "OnboardDate"]}
														fieldKey={[field.fieldKey, "OnboardDate"]}
														rules={[{ required: true, message: "Select onboard date" }]}
														style={{ marginBottom: 0 }}
													>
														<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
													</Form.Item>
												),
											},
											{
												title: "Timeline (Months)",
												dataIndex: "ProjectTimeLine",
												width: 120,
												render: (_, field) => (
													<Form.Item
														name={[field.name, "ProjectTimeLine"]}
														fieldKey={[field.fieldKey, "ProjectTimeLine"]}
														rules={[{ required: true, message: "Enter project timeline" }]}
														style={{ marginBottom: 0 }}
													>
														<InputNumber style={{ width: "100%" }} min={0} placeholder="Timeline" />
													</Form.Item>
												),
											},
											{
												title: "Actions",
												key: "actions",
												width: 110,
												render: (_, field) => (
													<Button
														type="default"
														danger
														shape="circle"
														icon={<MinusOutlined />}
														onClick={() => remove(field.name)}
														disabled={fields.length === 1}
													/>
												),
											},
										]}
										pagination={false}
										scroll={{ x: true }}
									/>
									<Button
										type="dashed"
										block
										icon={<PlusOutlined />}
										onClick={() => add(getEmptyRow())}
										style={{ marginTop: 16 }}
									>
										Add Another Site
									</Button>
								</>
							)}
						</Form.List>
					) : (
						<>
							<Form.Item
								label="Project Site Name"
								name="ProjectSiteName"
								rules={[{ required: true, message: "Enter project site name" }]}
							>
								<Input placeholder="Enter project site name" />
							</Form.Item>
							<Form.Item
								label="Project Types"
								name="ProjectTypeIds"
								rules={[{ required: true, message: "Select project types" }]}
							>
								<Select
									mode="multiple"
									placeholder="Select project types"
									options={projectTypeOptions}
									optionFilterProp="label"
									loading={typesLoading && !projectTypeOptions.length}
									allowClear
								/>
							</Form.Item>
							<Form.Item
								label="Onboard Date"
								name="OnboardDate"
								rules={[{ required: true, message: "Select onboard date" }]}
							>
								<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
							</Form.Item>
							<Form.Item
								label="Timeline (Months)"
								name="ProjectTimeLine"
								rules={[{ required: true, message: "Enter project timeline" }]}
							>
								<InputNumber style={{ width: "100%" }} min={0} placeholder="Timeline" />
							</Form.Item>
						</>
					)}
				</Form>
			</Modal>
		</div>
	);
};

export default ProjectSiteDetails;

